import { useState, useEffect } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { authClient } from "@/auth/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  SignOut,
  SquaresFour,
  ArrowRight,
  User as UserIcon,
} from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";

const navLinks = [
  { labelKey: "nav.forClients", href: "/", isRoute: true },
  { labelKey: "nav.therapists", href: "/therapists", isRoute: true },
  { labelKey: "nav.forTherapists", href: "/za-terapeute", isRoute: true },
  { labelKey: "nav.forCompanies", href: "/za-firme", isRoute: true },
  { labelKey: "Grupne terapije", href: "/grupne-terapije", isRoute: true },
  { labelKey: "nav.blog", href: "/blog", isRoute: true },
  { labelKey: "nav.video", href: "/video", isRoute: true },
  { labelKey: "nav.contact", href: "/kontakt", isRoute: true },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated, user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const location = useLocation();
  const isHome = location.pathname === "/";
  // Pages whose hero is a dark, full-bleed photo. The unscrolled header sits
  // over that surface with no bar of its own, so it switches to a
  // light-on-dark treatment for contrast. Home is petrol, /za-terapeute is umber.
  const hasDarkHero = isHome || location.pathname === "/za-terapeute";
  const onDark = hasDarkHero && !scrolled;

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Hash links rely on native browser scrolling (router scroll restoration is
  // off). When already on the home page, scroll client-side instead of doing a
  // full reload; off-home, let the browser navigate to "/#anchor" and scroll.
  function handleAnchorClick(e: React.MouseEvent, href: string) {
    if (!isHome) return;
    const id = href.slice(href.indexOf("#") + 1);
    const el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, "", href);
  }

  async function handleLogout() {
    await authClient.signOut();
    await queryClient.invalidateQueries({ queryKey: ["auth", "session"] });
    window.location.href = "/";
  }

  return (
    <>
      <header
        className="fixed top-0 z-50 w-full transition-all duration-500 ease-out"
        style={{
          background: scrolled
            ? "oklch(100% 0 0 / 0.92)"
            : hasDarkHero
              ? "transparent"
              : "oklch(100% 0 0 / 0.92)",
          backdropFilter:
            scrolled || !hasDarkHero ? "blur(20px) saturate(1.8)" : "none",
          borderBottom: scrolled
            ? "1px solid oklch(90% 0.02 84 / 0.6)"
            : "1px solid transparent",
          boxShadow: scrolled
            ? "0 1px 24px oklch(37.12% 0.059 186.62 / 0.06)"
            : "none",
        }}
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-5 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center"
            style={{
              color: onDark ? "oklch(99% 0 0)" : "oklch(15.52% 0.0263 186.62)",
            }}
          >
            <Logo className="h-7 w-auto transition-colors sm:h-8" title="Psihotron" />
          </Link>

          {/* Right cluster: nav + actions */}
          <div className="flex items-center gap-3 xl:gap-5">
            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center">
              {navLinks.map((link) => {
                const isActive =
                  link.isRoute && location.pathname === link.href;
                const inner = (
                  <span
                    className="relative block px-2.5 py-1.5 text-[0.8rem] font-medium whitespace-nowrap transition-opacity duration-200"
                    style={{
                      color: isActive
                        ? onDark
                          ? "oklch(100% 0 0)"
                          : "oklch(18.52% 0.0314 186.62)"
                        : onDark
                          ? "oklch(100% 0 0 / 0.7)"
                          : "oklch(45% 0.02 84)",
                    }}
                  >
                    {t(link.labelKey)}
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-x-2.5 -bottom-0.5 h-[2px] rounded-full"
                        style={{
                          background: onDark
                            ? "oklch(100% 0 0)"
                            : "oklch(37.12% 0.059 186.62)",
                        }}
                      />
                    )}
                  </span>
                );

                return link.isRoute ? (
                  <Link
                    key={link.labelKey}
                    to={link.href}
                    className="opacity-100 hover:opacity-70 transition-opacity"
                  >
                    {inner}
                  </Link>
                ) : (
                  <a
                    key={link.labelKey}
                    href={link.href}
                    onClick={(e) => handleAnchorClick(e, link.href)}
                    className="opacity-100 hover:opacity-70 transition-opacity"
                  >
                    {inner}
                  </a>
                );
              })}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2.5">
              {!isLoading && isAuthenticated ? (
                <>
                  {(user?.role === "admin" ||
                    user?.role === "therapist" ||
                    user?.role === "supervisor") && (
                    <Link
                      to="/dashboard"
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[0.825rem] font-medium transition-all duration-200 hover:opacity-90"
                      style={
                        onDark
                          ? {
                              color: "oklch(100% 0 0 / 0.92)",
                              background: "oklch(100% 0 0 / 0.14)",
                              border: "1px solid oklch(100% 0 0 / 0.22)",
                            }
                          : {
                              color: "oklch(42% 0.03 84)",
                              background: "oklch(96% 0.008 84 / 0.7)",
                              border: "1px solid oklch(90% 0.02 84 / 0.5)",
                            }
                      }
                    >
                      <SquaresFour className="size-3.5" />
                      {t("nav.dashboard")}
                    </Link>
                  )}
                  {(!user?.role ||
                    user.role === "user" ||
                    user.role === "client") && (
                    <Link
                      to="/profile"
                      aria-label={t("nav.profile")}
                      title={t("nav.profile")}
                      className="group relative inline-flex size-9 items-center justify-center overflow-hidden rounded-full transition-all duration-300 hover:scale-[1.05]"
                      style={
                        onDark
                          ? {
                              background: user?.image
                                ? "transparent"
                                : "oklch(100% 0 0 / 0.14)",
                              border: "1px solid oklch(100% 0 0 / 0.22)",
                            }
                          : {
                              background: user?.image
                                ? "transparent"
                                : "linear-gradient(135deg, oklch(37.12% 0.059 186.62 / 0.18), oklch(49.83% 0.0663 186.62 / 0.18))",
                              border: "1px solid oklch(90% 0.02 84 / 0.6)",
                              boxShadow:
                                "0 2px 10px oklch(37.12% 0.059 186.62 / 0.18)",
                            }
                      }
                    >
                      {user?.image ? (
                        <img
                          src={user.image}
                          alt=""
                          className="size-full object-cover"
                        />
                      ) : (
                        <span
                          className="text-[0.8rem] font-semibold leading-none"
                          style={{
                            color: onDark
                              ? "oklch(100% 0 0 / 0.92)"
                              : "oklch(45% 0.12 84)",
                            fontFamily: "var(--font-heading)",
                          }}
                        >
                          {(user?.name || user?.email || "?")
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      )}
                      <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                        style={{
                          boxShadow: onDark
                            ? "0 0 0 3px oklch(100% 0 0 / 0.16)"
                            : "0 0 0 3px oklch(37.12% 0.059 186.62 / 0.18)",
                        }}
                      />
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className={`hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[0.825rem] font-medium transition-all duration-200 ${
                      onDark
                        ? "hover:bg-[oklch(100%_0_0_/_0.12)]"
                        : "hover:bg-[oklch(96%_0.01_84)]"
                    }`}
                    style={{
                      color: onDark
                        ? "oklch(100% 0 0 / 0.85)"
                        : "oklch(50% 0.03 84)",
                    }}
                  >
                    <SignOut className="size-3.5" />
                    {t("nav.logout")}
                  </button>
                </>
              ) : !isLoading ? (
                <>
                  <Link
                    to="/login"
                    className="hidden sm:inline-flex rounded-full px-4 py-2 text-[0.825rem] font-medium transition-all duration-200"
                    style={{
                      color: onDark
                        ? "oklch(100% 0 0 / 0.85)"
                        : "oklch(42% 0.03 84)",
                    }}
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/therapist-enroll"
                    className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[0.825rem] font-semibold transition-opacity duration-200 hover:opacity-90"
                    style={
                      onDark
                        ? {
                            color: "oklch(23.52% 0.0399 186.62)",
                            background: "oklch(100% 0 0)",
                          }
                        : {
                            color: "oklch(100% 0 0)",
                            background: "oklch(37.12% 0.059 186.62)",
                          }
                    }
                  >
                    {t("nav.becomeTherapist")}
                    <ArrowRight className="size-3.5" />
                  </Link>
                </>
              ) : null}

              {/* Mobile hamburger */}
              <button
                type="button"
                className="inline-flex lg:hidden items-center justify-center rounded-xl p-2.5 transition-all duration-200"
                style={{
                  color: "oklch(35% 0.03 84)",
                  background: mobileMenuOpen
                    ? "oklch(96% 0.008 84)"
                    : onDark
                      ? "oklch(100% 0 0 / 0.12)"
                      : "transparent",
                }}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label={
                  mobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")
                }
              >
                <div className="relative size-5">
                  <span
                    className="absolute left-0 block h-[1.5px] w-5 rounded-full transition-all duration-300"
                    style={{
                      background: mobileMenuOpen
                        ? "oklch(30% 0.02 84)"
                        : onDark
                          ? "oklch(100% 0 0)"
                          : "oklch(30% 0.02 84)",
                      top: mobileMenuOpen ? "50%" : "30%",
                      transform: mobileMenuOpen
                        ? "translateY(-50%) rotate(45deg)"
                        : "none",
                    }}
                  />
                  <span
                    className="absolute left-0 top-1/2 block h-[1.5px] rounded-full transition-all duration-300"
                    style={{
                      background: mobileMenuOpen
                        ? "oklch(30% 0.02 84)"
                        : onDark
                          ? "oklch(100% 0 0)"
                          : "oklch(30% 0.02 84)",
                      width: mobileMenuOpen ? 0 : "14px",
                      opacity: mobileMenuOpen ? 0 : 1,
                      transform: "translateY(-50%)",
                    }}
                  />
                  <span
                    className="absolute left-0 block h-[1.5px] w-5 rounded-full transition-all duration-300"
                    style={{
                      background: mobileMenuOpen
                        ? "oklch(30% 0.02 84)"
                        : onDark
                          ? "oklch(100% 0 0)"
                          : "oklch(30% 0.02 84)",
                      top: mobileMenuOpen ? "50%" : "70%",
                      transform: mobileMenuOpen
                        ? "translateY(-50%) rotate(-45deg)"
                        : "none",
                    }}
                  />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile menu — full overlay */}
      <div
        className="fixed inset-0 z-40 lg:hidden transition-all duration-400"
        style={{
          opacity: mobileMenuOpen ? 1 : 0,
          pointerEvents: mobileMenuOpen ? "auto" : "none",
          visibility: mobileMenuOpen ? "visible" : "hidden",
        }}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{
            background: "oklch(0% 0 0 / 0.2)",
            backdropFilter: "blur(4px)",
            opacity: mobileMenuOpen ? 1 : 0,
          }}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* Panel */}
        <div
          className="absolute top-[3.5rem] left-0 right-0 overflow-hidden transition-all duration-400 ease-out"
          style={{
            maxHeight: mobileMenuOpen ? "calc(100vh - 3.5rem)" : 0,
            background: "oklch(100% 0 0 / 0.97)",
            backdropFilter: "blur(24px) saturate(1.8)",
            borderBottom: "1px solid oklch(90% 0.02 84 / 0.6)",
            boxShadow: "0 12px 40px oklch(0% 0 0 / 0.08)",
          }}
        >
          <nav className="mx-auto max-w-7xl px-5 py-6 sm:px-6">
            <div className="space-y-1">
              {navLinks.map((link, i) => {
                const isActive =
                  link.isRoute && location.pathname === link.href;
                const inner = (
                  <span
                    className="flex items-center gap-3 rounded-2xl px-4 py-3.5 text-[0.95rem] font-medium transition-all duration-200"
                    style={{
                      color: isActive
                        ? "oklch(25% 0.05 84)"
                        : "oklch(38% 0.03 84)",
                      background: isActive
                        ? "oklch(96% 0.015 84)"
                        : "transparent",
                    }}
                  >
                    <span
                      className="flex size-8 items-center justify-center rounded-xl text-xs font-bold"
                      style={{
                        background: isActive
                          ? "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))"
                          : "oklch(96% 0.008 84)",
                        color: isActive ? "white" : "oklch(55% 0.03 84)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {t(link.labelKey)}
                  </span>
                );

                return link.isRoute ? (
                  <Link
                    key={link.labelKey}
                    to={link.href}
                    className="block [&>span]:hover:bg-[oklch(96%_0.015_84)]"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {inner}
                  </Link>
                ) : (
                  <a
                    key={link.labelKey}
                    href={link.href}
                    className="block [&>span]:hover:bg-[oklch(96%_0.015_84)]"
                    onClick={(e) => {
                      setMobileMenuOpen(false);
                      handleAnchorClick(e, link.href);
                    }}
                  >
                    {inner}
                  </a>
                );
              })}
            </div>

            {/* Auth section */}
            <div
              className="mt-5 border-t pt-5"
              style={{ borderColor: "oklch(92% 0.015 84)" }}
            >
              {!isLoading && isAuthenticated ? (
                <div className="space-y-1">
                  {(!user?.role ||
                    user.role === "user" ||
                    user.role === "client") && (
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3.5 text-[0.95rem] font-medium transition-all duration-200 hover:bg-[oklch(96%_0.015_84)]"
                      style={{ color: "oklch(38% 0.03 84)" }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span
                        className="relative flex size-8 items-center justify-center overflow-hidden rounded-xl"
                        style={{
                          background: user?.image
                            ? "transparent"
                            : "linear-gradient(135deg, oklch(37.12% 0.059 186.62 / 0.18), oklch(49.83% 0.0663 186.62 / 0.18))",
                        }}
                      >
                        {user?.image ? (
                          <img
                            src={user.image}
                            alt=""
                            className="size-full object-cover"
                          />
                        ) : (
                          <UserIcon
                            className="size-4"
                            style={{ color: "oklch(55% 0.12 84)" }}
                          />
                        )}
                      </span>
                      {t("nav.profile")}
                    </Link>
                  )}
                  {(user?.role === "admin" ||
                    user?.role === "therapist" ||
                    user?.role === "supervisor") && (
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-3 rounded-2xl px-4 py-3.5 text-[0.95rem] font-medium transition-all duration-200 hover:bg-[oklch(96%_0.015_84)]"
                      style={{ color: "oklch(38% 0.03 84)" }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span
                        className="flex size-8 items-center justify-center rounded-xl"
                        style={{ background: "oklch(96% 0.008 84)" }}
                      >
                        <SquaresFour
                          className="size-4"
                          style={{ color: "oklch(55% 0.03 84)" }}
                        />
                      </span>
                      {t("nav.dashboard")}
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-[0.95rem] font-medium transition-all duration-200 hover:bg-[oklch(96%_0.015_84)]"
                    style={{ color: "oklch(50% 0.03 84)" }}
                  >
                    <span
                      className="flex size-8 items-center justify-center rounded-xl"
                      style={{ background: "oklch(96% 0.008 84)" }}
                    >
                      <SignOut
                        className="size-4"
                        style={{ color: "oklch(55% 0.03 84)" }}
                      />
                    </span>
                    {t("nav.logout")}
                  </button>
                </div>
              ) : !isLoading ? (
                <div className="flex items-stretch gap-2.5">
                  <Link
                    to="/login"
                    className="flex shrink-0 basis-[5.75rem] items-center justify-center rounded-2xl px-3 text-center text-[0.9rem] font-medium leading-none transition-all duration-200 sm:basis-[6.75rem] sm:text-[0.95rem]"
                    style={{
                      color: "oklch(38% 0.03 84)",
                      background: "oklch(96% 0.008 84)",
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.login")}
                  </Link>
                  <Link
                    to="/therapist-enroll"
                    className="flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl px-3 py-3.5 text-center text-[0.9rem] font-semibold text-white transition-all duration-200 sm:px-4 sm:text-[0.95rem]"
                    style={{
                      background:
                        "linear-gradient(135deg, oklch(37.12% 0.059 186.62), oklch(29.52% 0.0501 186.62))",
                      boxShadow: "0 2px 16px oklch(37.12% 0.059 186.62 / 0.3)",
                    }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t("nav.becomeTherapist")}
                    <ArrowRight className="size-3.5 shrink-0" />
                  </Link>
                </div>
              ) : null}
            </div>
          </nav>
        </div>
      </div>

      {/* Spacer — prevents content from hiding behind fixed header (not needed on home where hero is full-viewport) */}
      {!hasDarkHero && <div className="h-14" />}
    </>
  );
}
